import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { connectSocket, disconnectSocket } from './socketClient';
import { ensurePushSubscription } from './pushClient';
import { playAlertChime } from './alertSound';

const ALERT_AUTO_DISMISS_MS = 12000;
// Caps how many toasts can stack before the oldest is dropped — a burst of
// deliveries starting at once (e.g. several drivers heading out together at
// the start of a shift) should not fill the whole screen.
const MAX_STACKED_ALERTS = 4;

/**
 * The delivery-specific glue on top of the generic socketClient/pushClient
 * layer: opens the connection, requests push permission once, listens for the
 * two delivery events, and turns each into a stacked toast + chime.
 *
 * Lives in its own hook (rather than inline in AppShell) so a LATER module
 * wanting the same treatment for its own events copies this file's shape
 * instead of growing AppShell into a dumping ground for every feature's
 * real-time wiring.
 */
export function useDeliveryRealtime() {
    const { user, token } = useAuth();
    const [alerts, setAlerts] = useState([]);
    const timersRef = useRef({});

    const dismissAlert = useCallback((id) => {
        setAlerts((prev) => prev.filter((a) => a.id !== id));
        clearTimeout(timersRef.current[id]);
        delete timersRef.current[id];
    }, []);

    const pushAlert = useCallback((alert) => {
        setAlerts((prev) => [...prev.slice(-(MAX_STACKED_ALERTS - 1)), alert]);
        timersRef.current[alert.id] = setTimeout(() => dismissAlert(alert.id), ALERT_AUTO_DISMISS_MS);
        playAlertChime();
    }, [dismissAlert]);

    // Socket: connect whenever there's a user + token, and whenever the token
    // rotates (axios's silent refresh) — connectSocket() is a no-op if the
    // token it's given matches the one already connected, so this doesn't
    // reconnect on every render, only on an actual token change.
    useEffect(() => {
        if (!user || !token) {
            disconnectSocket();
            return undefined;
        }

        const socket = connectSocket(token);
        if (!socket) return undefined;

        const onStarted = (payload) => pushAlert({
            id: `started-${payload.deliveryId}-${Date.now()}`,
            kind: 'started',
            title: 'Delivery started',
            detail: `${payload.deliveryBoy || 'A driver'} is on the way to ${payload.customerName} (Invoice ${payload.invoiceNo})`
        });
        const onProof = (payload) => pushAlert({
            id: `proof-${payload.deliveryId}-${Date.now()}`,
            kind: 'proof',
            title: 'Proof of delivery uploaded',
            detail: `${payload.customerName} (Invoice ${payload.invoiceNo})`
        });
        // Only the assigned driver's socket is in this event's target list
        // (see createDelivery/updateDelivery's notifyUsers calls) — no role
        // check needed here. The bulk-upload sender (bulkUploadDeliveries)
        // carries `count` instead of a single delivery's identity, since one
        // summarized alert covers the whole batch rather than one per row.
        const onAssigned = (payload) => pushAlert({
            id: `assigned-${payload.deliveryId || 'bulk'}-${Date.now()}`,
            kind: 'assigned',
            title: payload.count ? 'New deliveries assigned' : 'New delivery assigned',
            detail: payload.count
                ? `${payload.count} new deliver${payload.count === 1 ? 'y' : 'ies'} assigned to you`
                : `${payload.customerName} — Invoice ${payload.invoiceNo}${payload.area ? ` (${payload.area})` : ''}`
        });

        socket.on('delivery:started', onStarted);
        socket.on('delivery:proof-uploaded', onProof);
        socket.on('delivery:assigned', onAssigned);

        return () => {
            socket.off('delivery:started', onStarted);
            socket.off('delivery:proof-uploaded', onProof);
            socket.off('delivery:assigned', onAssigned);
        };
    }, [user, token, pushAlert]);

    // Push: one attempt per session, a couple of seconds after the shell
    // settles rather than the instant it mounts — avoids racing the initial
    // burst of permission-sensitive bootstrapping (and, for a first-time
    // login, gives the user a moment to see the app before a browser prompt
    // interrupts them).
    useEffect(() => {
        if (!user) return undefined;
        const timer = setTimeout(() => { ensurePushSubscription(); }, 2000);
        return () => clearTimeout(timer);
    }, [user]);

    // Disconnect cleanly on unmount (e.g. logout unmounts AppShell) so a
    // stale socket doesn't sit in the browser after the user has signed out.
    useEffect(() => () => disconnectSocket(), []);

    return { alerts, dismissAlert };
}
